import { Circle, Heart, Trash2, Flame, Droplets, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EditableText } from "@/components/admin/EditableText";
import { EditablePrice } from "@/components/admin/EditablePrice";
import { useProductEditor } from "@/hooks/useProductEditor";
import { useToggleFavorite } from "@/hooks/useFavorites";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useState } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { optimizeImageUrl, imageSizes } from "@/lib/imageUtils";
import { getProPricing, isSaleActive } from "@/lib/proPricing";
import { SaleCountdown } from "@/components/product/SaleCountdown";
import { CircleScoreBadge } from "@/components/product/CircleScoreBadge";
import { CircleScore } from "@/contracts/circleScore";
import { isFeatureEnabled } from "@/lib/featureFlags";
import { PromoCodeBadge } from "@/components/product/PromoCodeBadge";
import { useProductPromoCodes } from "@/hooks/usePromoCodes";
import { requireUser } from "@/lib/requireUser";

interface DealCardProps {
  id: string;
  slug?: string;
  title: string;
  isFromDatabase?: boolean;
  subtitle: string;
  image: string;
  price: number;
  msrp: number;
  price_duration?: string;
  rating: number;
  review_count: number;
  categories: string[];
  is_select?: boolean;
  is_published?: boolean;
  isAdmin?: boolean;
  is_hot?: boolean;
  plansCount?: number;
  startingPrice?: number;
  sort_order?: number;
  isFavorited?: boolean; // Passed from parent, not fetched internally
  circleScore?: CircleScore | null;
}

export const DealCard = ({
  id,
  slug,
  title,
  subtitle,
  image,
  price,
  msrp,
  price_duration = 'mo',
  rating,
  review_count,
  categories,
  is_select = false,
  is_published = true,
  isFromDatabase = false,
  isAdmin = false,
  is_hot = false,
  plansCount = 0,
  startingPrice,
  sort_order = 999,
  isFavorited = false, // Use prop instead of hook
  circleScore = null,
}: DealCardProps) => {
  const { updateProduct, deleteProduct } = useProductEditor();
  const { user, membership } = useAuth();
  const navigate = useNavigate();
  const { openChatWithQuery } = useChat();
  const toggleFavorite = useToggleFavorite(slug || id);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { isEditMode } = useEditMode();
  
  // Fetch promo codes if feature enabled
  const { data: promoCodes } = useProductPromoCodes(id);
  const hasPromo = isFeatureEnabled('promoCodes') && promoCodes && promoCodes.length > 0;
  const bestPromo = hasPromo ? promoCodes[0] : null;
  
  // Calculate Pro pricing
  const product = { price, msrp, price_duration, pro_discount_mode: 'none' as any };
  const pro = getProPricing(product as any, membership);
  const saleActive = isSaleActive(product as any);

  const handleUpdateField = async (field: string, value: any) => {
    if (!isFromDatabase) {
      console.warn('Cannot edit mock products. Create products in database first.');
      return;
    }
    await updateProduct({ productId: slug || id, field, value });
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    requireUser({
      user,
      navigate,
      currentPath: window.location.pathname + window.location.search,
      action: () => toggleFavorite.mutate(),
      preferSignup: true,
    });
  };

  const handleToggleHot = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isFromDatabase) {
      console.warn('Cannot toggle hot status for non-database products');
      return;
    }
    await updateProduct({
      productId: slug || id,
      field: 'is_hot',
      value: !is_hot,
    });
  };

  const handleTogglePublish = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isFromDatabase) {
      console.warn('Cannot toggle publish status for non-database products');
      return;
    }
    await updateProduct({
      productId: slug || id,
      field: 'is_published',
      value: !is_published,
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    deleteProduct(slug || id);
    setShowDeleteDialog(false);
  };

  const handleSortOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 999 : parseInt(e.target.value);
    if (!isNaN(value) && isFromDatabase) {
      handleUpdateField('sort_order', value);
    }
  };

  return (
    <Link to={`/product/${slug || id}`} className="h-full block">
      <div className="group bg-card rounded-lg overflow-hidden border transition-all hover:shadow-hover h-full flex flex-col" onClick={(e) => {
        // Prevent navigation when clicking on edit elements
        if ((e.target as HTMLElement).closest('.editable-wrapper')) {
          e.preventDefault();
        }
      }}>
        <div className="relative aspect-video overflow-hidden bg-secondary">
          <img
            src={optimizeImageUrl(image, imageSizes.card)}
            alt={title}
            width={400}
            height={300}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
          
          {/* Sort Order Input - Compact Circle */}
          {isAdmin && isEditMode && (
            <div className="absolute top-2 left-2 z-20 p-2" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
              <input 
                type="number" 
                value={sort_order === 999 ? '' : sort_order}
                placeholder="#"
                onChange={handleSortOrderChange}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                className="w-12 h-12 text-sm font-medium text-center bg-background/95 backdrop-blur-sm border-2 border-border focus:outline-none focus:ring-2 focus:ring-primary rounded-full shadow-md cursor-text"
                min="0"
                title="Sort Order (lower = higher priority)"
              />
            </div>
          )}
          
          {isAdmin && isEditMode && (
            <div className="absolute top-2 right-2 flex flex-col md:flex-row gap-1 md:gap-2 z-20">
              <Badge 
                variant={is_published ? "default" : "secondary"}
                className={is_published ? "bg-green-600 hover:bg-green-700 cursor-pointer text-xs md:text-sm px-2 py-0.5 font-bold" : "bg-amber-500 hover:bg-amber-600 cursor-pointer text-xs md:text-sm px-2 py-0.5 font-bold"}
                onClick={handleTogglePublish}
              >
                {is_published ? "P" : "U"}
              </Badge>
              <Badge
                variant={is_hot ? "default" : "outline"}
                className={is_hot ? "bg-orange-600 hover:bg-orange-700 cursor-pointer text-xs md:text-sm px-2 py-0.5" : "cursor-pointer hover:bg-orange-50 text-xs md:text-sm px-2 py-0.5 bg-white"}
                onClick={handleToggleHot}
              >
                {is_hot ? <Flame className="h-3 w-3 md:h-4 md:w-4" /> : <Droplets className="h-3 w-3 md:h-4 md:w-4" />}
              </Badge>
              <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogTrigger asChild>
                  <Badge 
                    variant="destructive"
                    className="cursor-pointer hover:bg-destructive/90 px-1.5 py-0.5"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Badge>
                </AlertDialogTrigger>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Product?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete "{title}" and all associated data including plans, reviews, Q&A entries, and features. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete Product
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          
          {/* Favorite button - always show */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 bg-background/80 hover:bg-background z-10 group"
            onClick={handleFavoriteClick}
          >
            <Heart className={`h-4 w-4 transition-colors ${isFavorited ? 'fill-red-500 text-red-500' : 'text-muted-foreground group-hover:text-red-500'}`} />
          </Button>
        </div>

        <div className="p-2 md:p-4 flex-1 flex flex-col">
          <div className="editable-wrapper mb-1">
            <EditableText
              value={title}
              onSave={(value) => handleUpdateField('title', value)}
              as="h3"
              className="font-bold text-sm md:text-lg group-hover:text-blue-theme transition-colors"
              disabled={!isFromDatabase}
            />
          </div>
          
          <div className="editable-wrapper mb-2 md:mb-3">
            <EditableText
              value={subtitle}
              onSave={(value) => handleUpdateField('subtitle', value)}
              as="p"
              className="text-xs md:text-sm text-muted-foreground line-clamp-2 md:line-clamp-3"
              multiline
              disabled={!isFromDatabase}
            />
          </div>

          {/* Circle Score Badge */}
          {isFeatureEnabled('circleScore') && circleScore && circleScore.circle_score && (
            <div className="mb-2 md:mb-3">
              <CircleScoreBadge score={circleScore} compact />
            </div>
          )}
          
          {/* Promo Badge */}
          {hasPromo && bestPromo && (
            <div className="mb-2">
              <PromoCodeBadge promo={bestPromo} />
            </div>
          )}

          {review_count > 0 && (
            <div className="flex items-center gap-0.5 md:gap-1 mb-2 md:mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Circle
                  key={i}
                  className={`h-3 w-3 md:h-4 md:w-4 ${
                    i < Math.floor(rating)
                      ? "fill-deal-badge text-deal-badge"
                      : "fill-muted text-muted"
                  }`}
                />
              ))}
              <span className="text-xs md:text-sm font-medium ml-1">
                {rating.toFixed(1)}
              </span>
              <span className="text-xs md:text-sm text-muted-foreground ml-1">
                ({review_count} {review_count === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          )}

          <div className="editable-wrapper mt-auto space-y-2">
            {price_duration === 'custom' || ((!price || price === 0) && (!startingPrice || startingPrice === 0)) ? (
              <div className="space-y-1">
                <span className="text-sm font-bold text-foreground">
                  Custom Pricing
                </span>
                <p className="text-xs text-muted-foreground">
                  Contact for quote
                </p>
              </div>
            ) : (
              <>
                {/* Sale countdown */}
                {saleActive && (product as any).sale_ends_at && (
                  <SaleCountdown endsAt={(product as any).sale_ends_at} compact />
                )}
                
                {/* Base price */}
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground">Starting at</span>
                  <div className="flex items-baseline gap-0.5 md:gap-1 flex-wrap">
                    <EditablePrice
                      value={pro.basePrice ?? startingPrice ?? price}
                      onSave={(value) => handleUpdateField('price', value)}
                      className="text-lg md:text-2xl font-bold"
                      disabled={!isFromDatabase}
                    />
                    <span className="text-sm md:text-base font-normal">/</span>
                    <EditableText
                      value={price_duration}
                      onSave={(value) => handleUpdateField('price_duration', value)}
                      as="span"
                      className="text-sm md:text-base font-normal"
                      disabled={!isFromDatabase}
                    />
                    {pro.retailPrice && pro.retailPrice > (pro.basePrice ?? startingPrice ?? price) && (
                      <EditablePrice
                        value={pro.retailPrice}
                        onSave={(value) => handleUpdateField('msrp', value)}
                        className="text-xs md:text-sm text-muted-foreground line-through ml-1.5"
                        disabled={!isFromDatabase}
                      />
                    )}
                  </div>
                </div>
                
                {/* Pro instant discount */}
                {(pro.mode === 'instant' || pro.mode === 'vendorAdjusted') && pro.proPrice && (
                  <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/20 p-2 rounded border border-emerald-200 dark:border-emerald-800">
                    <Badge variant="secondary" className="bg-emerald-600 text-white text-xs">PRO</Badge>
                    <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      ${pro.proPrice}/{price_duration}
                    </span>
                    {pro.savingsAmount && (
                      <span className="text-xs text-emerald-600 dark:text-emerald-500">
                        Save ${pro.savingsAmount}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Pro rebate */}
                {pro.mode === 'rebate' && pro.rebateAmount && (
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Get ${pro.rebateAmount} back
                  </div>
                )}
                
                {/* Non-Pro teaser */}
                {!pro.hasSavings && pro.basePrice && pro.basePrice > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Join Pro to save ~${Math.round(pro.basePrice * 0.1)}/{price_duration}
                  </div>
                )}
                
                {plansCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {plansCount} pricing option{plansCount !== 1 ? 's' : ''} available
                  </p>
                )}
              </>
            )}
            
            <div className="min-h-[22px] mt-1">
              {is_hot && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-950 dark:text-orange-400">
                  <Flame className="h-3 w-3 mr-1" />
                  Hot Deal
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-2 md:mt-3 min-h-[28px] flex items-center gap-1 text-xs font-semibold">
            {is_select && (
              <>
                <span className="font-handelson font-extralight bg-deal-badge text-deal-badge-foreground px-3 py-1 rounded-md text-xl leading-none flex items-center justify-center">
                  Circle
                </span>
                <span className="text-xs">SELECT</span>
              </>
            )}
          </div>
          
          {/* AskCircle CTA - appears on hover */}
          <div className="mt-2 md:mt-3 pt-2 border-t opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openChatWithQuery(`Tell me about ${title}`, title);
              }}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex flex-wrap items-center gap-x-1 gap-y-0 group/link w-full text-left"
            >
              <span className="whitespace-nowrap">Not sure if this is right?</span>
              <span className="flex items-baseline whitespace-nowrap">
                <span>→ Ask</span>
                <span className="font-handelson font-light text-sm -ml-0.5">Circle</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
};
